/**
 * Low-level Woods helpers — golden tests and model plugins share this path.
 * Runtime stepping goes through model plugins (see plugins/models).
 */

import { softDirection } from "./field";
import type { SimParams } from "./params";
import { getRelationProfile } from "./relationProfile";
import type { Rng } from "./rng";
import type { SpatialHash2D } from "./spatialHash";
import {
  eligibleIndices,
  expandTypeRules,
  sampleEligible,
  type TypeRulesNormalized,
} from "./typeRules";

export function woodsStep(
  positions: Float64Array,
  friends: Int32Array,
  enemies: Int32Array,
  params: SimParams,
  out: Float64Array,
  scratchPull: Float64Array,
  scratchPush: Float64Array,
): void {
  const { dim, contraction, friendStep, enemyStep, softening } = params;
  const n = friends.length;

  softDirection(positions, friends, dim, softening, scratchPull);
  softDirection(positions, enemies, dim, softening, scratchPush);

  const len = n * dim;
  for (let i = 0; i < len; i++) {
    out[i] =
      contraction * positions[i]! +
      friendStep * scratchPull[i]! -
      enemyStep * scratchPush[i]!;
  }
}

/**
 * Generic signed role sum — used when not classic_fe fused path.
 * For R=2 classic, prefer woodsStep for golden bit-identity.
 */
export function woodsStepRoles(
  positions: Float64Array,
  targets: Int32Array,
  roleWeights: Float64Array,
  n: number,
  roleCount: number,
  dim: number,
  contraction: number,
  softening: number,
  out: Float64Array,
  scratch: Float64Array,
): void {
  const len = n * dim;
  for (let i = 0; i < len; i++) {
    out[i] = contraction * positions[i]!;
  }
  for (let r = 0; r < roleCount; r++) {
    const plane = targets.subarray(r * n, r * n + n);
    softDirection(positions, plane, dim, softening, scratch);
    const w = roleWeights[r]!;
    for (let i = 0; i < len; i++) {
      out[i]! += w * scratch[i]!;
    }
  }
}

function sampleTarget(
  rng: Rng,
  n: number,
  selfIdx: number,
  forbidSelf: boolean,
): number {
  if (!forbidSelf || n === 1) return rng.int(n);
  let j = rng.int(n - 1);
  if (j >= selfIdx) j += 1;
  return j;
}

/** Scratch for distinct rechoose indices (no Set / array alloc on hot path). */
const pickBuf = new Int32Array(64);

const neighborScratch: number[] = [];

export function rechoose(
  friends: Int32Array,
  enemies: Int32Array,
  params: SimParams,
  rng: Rng,
  opts?: {
    positions?: Float64Array;
    spatialHash?: SpatialHash2D | null;
  },
): void {
  if (
    params.freezeTopology ||
    params.rechooseProb <= 0 ||
    params.rechooseCount <= 0
  ) {
    return;
  }
  if (rng.next() >= params.rechooseProb) return;

  const n = friends.length;
  const count = Math.min(params.rechooseCount, n, pickBuf.length);
  const local =
    params.localTopology &&
    opts?.spatialHash != null &&
    opts.positions != null &&
    params.dim >= 2;

  for (let i = 0; i < count; i++) {
    let cand = rng.int(n);
    let guard = 0;
    while (guard++ < 24) {
      let ok = true;
      for (let k = 0; k < i; k++) {
        if (pickBuf[k] === cand) {
          ok = false;
          break;
        }
      }
      if (ok) break;
      cand = rng.int(n);
    }
    pickBuf[i] = cand;
  }

  for (let k = 0; k < count; k++) {
    const i = pickBuf[k]!;
    const pick = (): number => {
      if (local) {
        opts!.spatialHash!.queryIndices(
          i,
          opts!.positions!,
          params.dim,
          params.neighborRadius,
          neighborScratch,
        );
        if (neighborScratch.length > 0) {
          return neighborScratch[rng.int(neighborScratch.length)]!;
        }
        // empty neighborhood → global fallback (documented in DESIGNS-TRACK-X §5)
      }
      return sampleTarget(rng, n, i, params.forbidSelfLinks);
    };

    if (params.rechooseMode === "coupled") {
      const j = pick();
      friends[i] = j;
      enemies[i] = j;
    } else {
      friends[i] = pick();
      enemies[i] = pick();
    }
  }
}

const eligScratch: number[] = [];
const eligCoupled: number[] = [];

/**
 * Type-aware rechoose when typeCount > 1 and types present (browser-first).
 * Mutates planar targets in place. freezeTopology still wins.
 */
export function rechooseRoles(
  targets: Int32Array,
  n: number,
  roleCount: number,
  params: SimParams,
  rng: Rng,
  types: Uint8Array | null,
  opts?: {
    positions?: Float64Array;
    spatialHash?: SpatialHash2D | null;
  },
): void {
  // Classic unconstrained path when no multi-type
  if (!types || params.typeCount <= 1) {
    const friends = targets.subarray(0, n);
    const enemies =
      roleCount >= 2 ? targets.subarray(n, 2 * n) : targets.subarray(0, 0);
    rechoose(friends, enemies, params, rng, opts);
    return;
  }

  if (
    params.freezeTopology ||
    params.rechooseProb <= 0 ||
    params.rechooseCount <= 0
  ) {
    return;
  }
  if (rng.next() >= params.rechooseProb) return;

  let rules: TypeRulesNormalized;
  try {
    rules = expandTypeRules(params);
  } catch {
    // Bad rules — fall back to classic rechoose on planes
    const friends = targets.subarray(0, n);
    const enemies =
      roleCount >= 2 ? targets.subarray(n, 2 * n) : targets.subarray(0, 0);
    rechoose(friends, enemies, params, rng, opts);
    return;
  }

  const profile = getRelationProfile(params.relationProfile);
  const count = Math.min(params.rechooseCount, n, pickBuf.length);
  const forbid = params.forbidSelfLinks;

  for (let i = 0; i < count; i++) {
    let cand = rng.int(n);
    let guard = 0;
    while (guard++ < 24) {
      let ok = true;
      for (let k = 0; k < i; k++) {
        if (pickBuf[k] === cand) {
          ok = false;
          break;
        }
      }
      if (ok) break;
      cand = rng.int(n);
    }
    pickBuf[i] = cand;
  }

  for (let k = 0; k < count; k++) {
    const i = pickBuf[k]!;
    if (params.rechooseMode === "coupled") {
      // Intersect eligibles across roles; if empty, independent per role
      eligCoupled.length = 0;
      let first = true;
      for (let r = 0; r < roleCount; r++) {
        const roleId = profile.roles[r]?.id ?? (r === 0 ? "friend" : "enemy");
        eligibleIndices(i, roleId, types, rules, n, forbid, eligScratch);
        if (first) {
          eligCoupled.push(...eligScratch);
          first = false;
        } else {
          const set = new Set(eligScratch);
          for (let z = eligCoupled.length - 1; z >= 0; z--) {
            if (!set.has(eligCoupled[z]!)) eligCoupled.splice(z, 1);
          }
        }
      }
      if (eligCoupled.length > 0) {
        const j = eligCoupled[rng.int(eligCoupled.length)]!;
        for (let r = 0; r < roleCount; r++) {
          targets[r * n + i] = j;
        }
      } else {
        for (let r = 0; r < roleCount; r++) {
          const roleId = profile.roles[r]?.id ?? (r === 0 ? "friend" : "enemy");
          eligibleIndices(i, roleId, types, rules, n, forbid, eligScratch);
          targets[r * n + i] = sampleEligible(rng, eligScratch, n, i, forbid);
        }
      }
    } else {
      for (let r = 0; r < roleCount; r++) {
        const roleId = profile.roles[r]?.id ?? (r === 0 ? "friend" : "enemy");
        eligibleIndices(i, roleId, types, rules, n, forbid, eligScratch);
        targets[r * n + i] = sampleEligible(rng, eligScratch, n, i, forbid);
      }
    }
  }
}
